import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, Check, MapPin, Clock, Home, Sprout, RefreshCw, Crown, Lock, Sparkles } from 'lucide-react';
import { UpgradeDialog } from '@/components/UpgradeDialog';
import { PlantSelector } from '@/components/PlantSelector';
import { ArchetypeSelector, ARCHETYPES } from '@/components/ArchetypeSelector';

interface Plant {
  id: string;
  name: string;
  category: string;
  description: string | null;
  difficulty: string | null;
  plant_type: string;
}

const UK_ZONES = [
  { value: 'UK-South', label: 'South England', description: 'London, Bristol, Southampton' },
  { value: 'UK-Midlands', label: 'Midlands', description: 'Birmingham, Nottingham, Leicester' },
  { value: 'UK-North', label: 'North England', description: 'Manchester, Leeds, Newcastle' },
  { value: 'UK-Scotland', label: 'Scotland', description: 'Edinburgh, Glasgow, Aberdeen' },
  { value: 'UK-Wales', label: 'Wales', description: 'Cardiff, Swansea, Bangor' },
  { value: 'UK-NI', label: 'Northern Ireland', description: 'Belfast, Derry' },
];

const SETUP_OPTIONS = [
  { value: 'windowsill', label: 'Windowsill', icon: '🪟', description: 'Growing on windowsills and indoors' },
  { value: 'greenhouse', label: 'Greenhouse', icon: '🏡', description: 'Have a greenhouse or polytunnel' },
  { value: 'cold_frame', label: 'Cold Frame', icon: '📦', description: 'Using cold frames for hardening off' },
  { value: 'pots', label: 'Pots / Containers', icon: '🪴', description: 'Plant pots on balconies or patio' },
  { value: 'direct', label: 'Direct / Outdoor Beds', icon: '🌱', description: 'Planting straight into beds or ground' },
];


export default function Settings() {
  const { user } = useAuth();
  const { isPro, freePlantLimit, canAddPlant } = useSubscription();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zone, setZone] = useState('');
  const [weekdayMinutes, setWeekdayMinutes] = useState(20);
  const [weekdayCount, setWeekdayCount] = useState(3);
  const [weekendHoursMin, setWeekendHoursMin] = useState(2);
  const [weekendHoursMax, setWeekendHoursMax] = useState(4);
  const [selectedSetups, setSelectedSetups] = useState<string[]>(['direct']);
  const [archetype, setArchetype] = useState('');
  const [allPlants, setAllPlants] = useState<Plant[]>([]);
  const [selectedPlants, setSelectedPlants] = useState<string[]>([]);
  const [establishedPlants, setEstablishedPlants] = useState<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadCurrentSettings();
  }, [user]);

  const loadCurrentSettings = async () => {
    const [profileRes, plantsRes, userPlantsRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('user_id', user!.id).single(),
      supabase.from('plants').select('id, name, category, description, difficulty, plant_type'),
      supabase.from('user_plants').select('plant_id, status').eq('user_id', user!.id),
    ]);

    if (profileRes.data) {
      const p = profileRes.data;
      setZone(p.growing_zone || '');
      setArchetype((p as any).gardener_archetype || '');
      setWeekdayMinutes(p.weekday_minutes || 20);
      setWeekdayCount(p.weekday_count || 3);
      setWeekendHoursMin(p.weekend_hours_min || 2);
      setWeekendHoursMax(p.weekend_hours_max || 4);
      setSelectedSetups(
        (p as any).available_setups || (p.setup_type ? [p.setup_type] : ['direct'])
      );
    }
    if (plantsRes.data) setAllPlants(plantsRes.data);
    if (userPlantsRes.data) {
      setSelectedPlants(userPlantsRes.data.map(up => up.plant_id));
      setEstablishedPlants(new Set(
        userPlantsRes.data.filter(up => up.status === 'active').map(up => up.plant_id)
      ));
    }
    setLoading(false);
  };

  const toggleSetup = (value: string) => {
    setSelectedSetups(prev =>
      prev.includes(value) ? prev.filter(s => s !== value) : [...prev, value]
    );
  };

  const togglePlant = (id: string) => {
    setSelectedPlants(prev => {
      if (prev.includes(id)) {
        // Also remove from established when deselecting
        setEstablishedPlants(s => { const n = new Set(s); n.delete(id); return n; });
        return prev.filter(p => p !== id);
      }
      // Check free tier limit
      if (!canAddPlant(prev.length)) {
        setShowUpgrade(true);
        return prev;
      }
      return [...prev, id];
    });
  };

  const toggleEstablished = (plantId: string) => {
    setEstablishedPlants(prev => {
      const next = new Set(prev);
      if (next.has(plantId)) next.delete(plantId);
      else next.add(plantId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      // Update profile
      await supabase.from('profiles').update({
        growing_zone: zone,
        gardener_archetype: archetype,
        weekday_minutes: weekdayMinutes,
        weekday_count: weekdayCount,
        weekend_hours_min: weekendHoursMin,
        weekend_hours_max: weekendHoursMax,
        setup_type: selectedSetups.length > 1 ? 'mixed' : (selectedSetups[0] || 'direct'),
        available_setups: selectedSetups,
      } as any).eq('user_id', user.id);

      // Sync plants: remove deselected, add new
      const { data: existingPlants } = await supabase
        .from('user_plants')
        .select('id, plant_id')
        .eq('user_id', user.id);

      const existingIds = (existingPlants || []).map(p => p.plant_id);
      const toRemove = (existingPlants || []).filter(p => !selectedPlants.includes(p.plant_id));
      const toAdd = selectedPlants.filter(id => !existingIds.includes(id));

      if (toRemove.length > 0) {
        await supabase.from('user_plants').delete().in('id', toRemove.map(p => p.id));
        await supabase.from('garden_tasks').delete().in('user_plant_id', toRemove.map(p => p.id));
      }

      if (toAdd.length > 0) {
        await supabase.from('user_plants').insert(
          toAdd.map(plant_id => ({
            user_id: user.id,
            plant_id,
            status: establishedPlants.has(plant_id) ? 'active' : 'planned',
          }))
        );
      }

      // Update status for existing plants that changed established state
      const existingKept = (existingPlants || []).filter(p => selectedPlants.includes(p.plant_id));
      for (const ep of existingKept) {
        const shouldBeEstablished = establishedPlants.has(ep.plant_id);
        await supabase.from('user_plants').update({
          status: shouldBeEstablished ? 'active' : 'planned',
        }).eq('id', ep.id);
      }

      toast.success('Settings saved! Regenerate your schedule to apply changes.');
      navigate('/');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background botanical-dots">
        <Sprout className="h-8 w-8 animate-gentle-bounce text-primary" />
      </div>
    );
  }

  const selectedArchetypeData = ARCHETYPES.find(a => a.value === archetype);

  const sections = [
    { id: 'location', icon: MapPin, label: 'Location', summary: UK_ZONES.find(z => z.value === zone)?.label || 'Not set' },
    { id: 'plants', icon: Sprout, label: 'Plants', summary: `${selectedPlants.length}${!isPro ? `/${freePlantLimit}` : ''} selected${isPro ? ' (Pro)' : ''}` },
    { id: 'time', icon: Clock, label: 'Time', summary: `${weekdayMinutes}min × ${weekdayCount} days + ${weekendHoursMin}-${weekendHoursMax}h weekends` },
    { id: 'setup', icon: Home, label: 'Setup', summary: selectedSetups.map(s => SETUP_OPTIONS.find(o => o.value === s)?.label).filter(Boolean).join(', ') },
    { id: 'archetype', icon: Sparkles, label: 'Gardener Archetype', summary: selectedArchetypeData ? `${selectedArchetypeData.emoji} ${selectedArchetypeData.label}` : 'Not set' },
  ];

  return (
    <div className="min-h-screen bg-background botanical-dots p-4 md:p-8">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-xl">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Settings</h1>
            <p className="text-sm text-muted-foreground">Edit your garden preferences</p>
          </div>
        </div>

        <div className="space-y-3">
          {sections.map((section, i) => (
            <div key={section.id} className="animate-fade-in" style={{ animationDelay: `${i * 0.05}s`, animationFillMode: 'both' }}>
              <button
                onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
                className="flex w-full items-center gap-4 rounded-2xl border border-border/60 bg-card p-4 text-left shadow-soft transition-all hover:shadow-medium hover:border-primary/30"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <section.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-foreground">{section.label}</div>
                  <div className="text-sm text-muted-foreground">{section.summary}</div>
                </div>
                <ChevronLeft className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${activeSection === section.id ? '-rotate-90' : ''}`} />
              </button>

              {activeSection === section.id && (
                <Card className="mt-2 card-organic border-primary/15 animate-fade-in-scale">
                  <CardContent className="pt-4">
                    {section.id === 'location' && (
                      <div className="grid gap-2">
                        {UK_ZONES.map(z => (
                          <button
                            key={z.value}
                            onClick={() => setZone(z.value)}
                            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                              zone === z.value ? 'border-primary bg-primary/5 shadow-soft' : 'border-border/60 hover:border-primary/30 hover:shadow-soft'
                            }`}
                          >
                            <MapPin className={`h-4 w-4 ${zone === z.value ? 'text-primary' : 'text-muted-foreground'}`} />
                            <div>
                              <div className="text-sm font-semibold text-foreground">{z.label}</div>
                              <div className="text-xs text-muted-foreground">{z.description}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {section.id === 'plants' && (
                      <div>
                        {!isPro && (
                          <div className="mb-3 flex items-center gap-2 rounded-xl bg-muted/60 p-3 text-sm">
                            <Lock className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="text-muted-foreground">
                              Free plan: <strong className="text-foreground">{selectedPlants.length}/{freePlantLimit}</strong> plants used.{' '}
                              <button onClick={() => setShowUpgrade(true)} className="text-primary font-medium hover:underline">
                                Upgrade for unlimited
                              </button>
                            </span>
                          </div>
                        )}
                        {isPro && (
                          <div className="mb-3 flex items-center gap-2 rounded-xl bg-primary/5 p-3 text-sm">
                            <Crown className="h-4 w-4 text-primary shrink-0" />
                            <span className="text-foreground font-medium">Pro — Unlimited plants</span>
                          </div>
                        )}
                        <PlantSelector
                          plants={allPlants}
                          selectedPlants={selectedPlants}
                          establishedPlants={establishedPlants}
                          onTogglePlant={togglePlant}
                          onToggleEstablished={toggleEstablished}
                          atLimit={(id) => !isPro && !selectedPlants.includes(id) && selectedPlants.length >= freePlantLimit}
                        />
                      </div>
                    )}

                    {section.id === 'time' && (
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <label className="text-sm font-medium text-foreground">Weekday gardening time</label>
                          <div className="flex items-center gap-4">
                            <Slider value={[weekdayMinutes]} onValueChange={([v]) => setWeekdayMinutes(v)} min={5} max={60} step={5} className="flex-1" />
                            <span className="w-20 text-right font-semibold text-primary">{weekdayMinutes} min</span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <label className="text-sm font-medium text-foreground">How many weekdays?</label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map(n => (
                              <button key={n} onClick={() => setWeekdayCount(n)}
                                className={`flex h-10 w-10 items-center justify-center rounded-xl border text-sm font-semibold transition-all ${
                                  weekdayCount === n ? 'border-primary bg-primary text-primary-foreground shadow-soft' : 'border-border/60 text-foreground hover:border-primary/30'
                                }`}
                              >{n}</button>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-3">
                          <label className="text-sm font-medium text-foreground">Weekend hours</label>
                          <div className="flex items-center gap-4">
                            <span className="w-8 text-xs text-muted-foreground">Min</span>
                            <Slider value={[weekendHoursMin]} onValueChange={([v]) => { setWeekendHoursMin(v); if (v > weekendHoursMax) setWeekendHoursMax(v); }} min={0.5} max={8} step={0.5} className="flex-1" />
                            <span className="w-12 text-right text-sm font-semibold text-primary">{weekendHoursMin}h</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="w-8 text-xs text-muted-foreground">Max</span>
                            <Slider value={[weekendHoursMax]} onValueChange={([v]) => { setWeekendHoursMax(v); if (v < weekendHoursMin) setWeekendHoursMin(v); }} min={0.5} max={8} step={0.5} className="flex-1" />
                            <span className="w-12 text-right text-sm font-semibold text-primary">{weekendHoursMax}h</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {section.id === 'setup' && (
                      <div className="grid gap-2">
                        {SETUP_OPTIONS.map(s => (
                          <button
                            key={s.value}
                            onClick={() => toggleSetup(s.value)}
                            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                              selectedSetups.includes(s.value) ? 'border-primary bg-primary/5 shadow-soft' : 'border-border/60 hover:border-primary/30 hover:shadow-soft'
                            }`}
                          >
                            <span className="text-xl">{s.icon}</span>
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-foreground">{s.label}</div>
                              <div className="text-xs text-muted-foreground">{s.description}</div>
                            </div>
                            {selectedSetups.includes(s.value) && <Check className="h-4 w-4 text-primary" />}
                          </button>
                        ))}
                      </div>
                    )}

                    {section.id === 'archetype' && (
                      <ArchetypeSelector selected={archetype} onSelect={setArchetype} />
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-3">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={() => navigate('/')}>Cancel</Button>
          <Button className="flex-1 garden-gradient rounded-xl shadow-soft hover:shadow-medium transition-all hover:scale-[1.01] active:scale-[0.99]" onClick={handleSave} disabled={saving || selectedPlants.length === 0 || selectedSetups.length === 0}>
            {saving ? 'Saving...' : 'Save & Return'}
          </Button>
        </div>

        <p className="mt-3 text-center text-xs text-muted-foreground">
          After saving, regenerate your schedule — only future tasks will be updated
        </p>

        <button
          onClick={() => navigate('/subscription')}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-border/60 bg-card p-4 text-sm font-medium text-foreground shadow-soft transition-all hover:shadow-medium hover:border-primary/30"
        >
          <Crown className="h-4 w-4 text-primary" />
          {isPro ? 'Manage Subscription' : 'View Plans & Pricing'}
        </button>
      </div>
      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
    </div>
  );
}
